<div class="support-ticket-list-container">
  <!-- Header Section -->
  <div class="header-section">
    <div class="header-content">
      <div class="title-section">
        <h1 class="page-title">
          <mat-icon class="title-icon">support_agent</mat-icon>
          Support Tickets
        </h1>
        <p class="page-subtitle">Manage and track support requests</p>
      </div>
      
      <div class="header-actions">
        <button mat-raised-button color="primary" (click)="createNewTicket()" class="create-btn">
          <mat-icon>add</mat-icon>
          New Ticket
        </button>
        <button mat-icon-button (click)="refreshData()" [disabled]="loading" matTooltip="Refresh">
          <mat-icon>refresh</mat-icon>
        </button>
        <button mat-icon-button (click)="toggleStats()" matTooltip="Toggle Statistics">
          <mat-icon>{{ showStats ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <button mat-icon-button (click)="toggleFilters()" matTooltip="Toggle Filters">
          <mat-icon>filter_list</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Statistics Section -->
  <div class="stats-section" *ngIf="showStats">
    <div class="stats-grid" *ngIf="!loadingStats && stats">
      <div class="stat-card total">
        <div class="stat-icon">
          <mat-icon>confirmation_number</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total_tickets }}</div>
          <div class="stat-label">Total Tickets</div>
        </div>
      </div>

      <div class="stat-card open">
        <div class="stat-icon">
          <mat-icon>pending</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.open_tickets }}</div>
          <div class="stat-label">Open</div>
        </div>
      </div>

      <div class="stat-card in-progress">
        <div class="stat-icon">
          <mat-icon>engineering</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.in_progress_tickets }}</div>
          <div class="stat-label">In Progress</div>
        </div>
      </div>

      <div class="stat-card resolved">
        <div class="stat-icon">
          <mat-icon>check_circle</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.resolved_tickets }}</div>
          <div class="stat-label">Resolved</div>
        </div>
      </div>

      <div class="stat-card critical">
        <div class="stat-icon">
          <mat-icon>priority_high</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.critical_tickets }}</div>
          <div class="stat-label">Critical</div>
        </div>
      </div>

      <div class="stat-card avg-time">
        <div class="stat-icon">
          <mat-icon>schedule</mat-icon>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.avg_resolution_hours | number:'1.1-1' }}h</div>
          <div class="stat-label">Avg Resolution</div>
        </div>
      </div>
    </div>

    <div class="stats-loading" *ngIf="loadingStats">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Loading statistics...</span>
    </div>
  </div>

  <!-- Search and Filters Section -->
  <div class="search-filters-section">
    <!-- Search Bar -->
    <div class="search-section">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search tickets...</mat-label>
        <input matInput 
               [(ngModel)]="searchTerm" 
               (input)="onSearchChange($event)"
               placeholder="Search by ticket number, name, description, or department">
        <mat-icon matSuffix>search</mat-icon>
        <button mat-icon-button matSuffix *ngIf="searchTerm" (click)="clearSearch()" matTooltip="Clear search">
          <mat-icon>clear</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <!-- Filters Panel -->
    <div class="filters-panel" *ngIf="showFilters">
      <form [formGroup]="filterForm" class="filters-form">
        <div class="filters-grid">
          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              <mat-option value="">All Statuses</mat-option>
              <mat-option *ngFor="let status of statuses" [value]="status.name">
                {{ status.name | titlecase }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Priority</mat-label>
            <mat-select formControlName="priority">
              <mat-option value="">All Priorities</mat-option>
              <mat-option *ngFor="let priority of priorities" [value]="priority.name">
                {{ priority.name | titlecase }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Category</mat-label>
            <mat-select formControlName="category">
              <mat-option value="">All Categories</mat-option>
              <mat-option *ngFor="let category of categories" [value]="category.name">
                {{ category.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Start Date</mat-label>
            <input matInput [matDatepicker]="startPicker" formControlName="start_date">
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>End Date</mat-label>
            <input matInput [matDatepicker]="endPicker" formControlName="end_date">
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Sort By</mat-label>
            <mat-select formControlName="sort_by">
              <mat-option value="created_at">Created Date</mat-option>
              <mat-option value="updated_at">Updated Date</mat-option>
              <mat-option value="ticket_number">Ticket Number</mat-option>
              <mat-option value="name">Name</mat-option>
              <mat-option value="status">Status</mat-option>
              <mat-option value="priority">Priority</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Sort Order</mat-label>
            <mat-select formControlName="sort_order">
              <mat-option value="DESC">Descending</mat-option>
              <mat-option value="ASC">Ascending</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="filters-actions">
          <button mat-button (click)="clearFilters()" class="clear-filters-btn">
            <mat-icon>clear_all</mat-icon>
            Clear Filters
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Tickets Table Section -->
  <div class="tickets-section">
    <div class="table-container">
      <!-- Loading State -->
      <div class="loading-container" *ngIf="loading">
        <mat-spinner diameter="50"></mat-spinner>
        <p>Loading tickets...</p>
      </div>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="!loading && tickets.length === 0">
        <mat-icon class="empty-icon">support_agent</mat-icon>
        <h3>No tickets found</h3>
        <p>No support tickets match your current filters.</p>
        <button mat-raised-button color="primary" (click)="createNewTicket()">
          Create First Ticket
        </button>
      </div>

      <!-- Tickets Table -->
      <table mat-table [dataSource]="tickets" class="tickets-table" *ngIf="!loading && tickets.length > 0">
        <!-- Ticket Number Column -->
        <ng-container matColumnDef="ticket_number">
          <th mat-header-cell *matHeaderCellDef>Ticket #</th>
          <td mat-cell *matCellDef="let ticket">
            <div class="ticket-number">
              <span class="number">{{ ticket.ticket_number }}</span>
              <span class="time-ago">{{ getTimeAgo(ticket.created_at!) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Name Column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Name</th>
          <td mat-cell *matCellDef="let ticket">
            <div class="ticket-name">
              <div class="name">{{ ticket.name }}</div>
              <div class="department">{{ ticket.department }}</div>
            </div>
          </td>
        </ng-container>

        <!-- Category Column -->
        <ng-container matColumnDef="category">
          <th mat-header-cell *matHeaderCellDef>Category</th>
          <td mat-cell *matCellDef="let ticket">
            <span class="category-badge">{{ ticket.ticket_category }}</span>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let ticket">
            <span class="status-badge" 
                  [style.background-color]="getStatusColor(ticket.status!)"
                  [style.color]="ticket.status === 'open' ? '#fff' : '#000'">
              {{ getStatusText(ticket.status!) }}
            </span>
          </td>
        </ng-container>

        <!-- Priority Column -->
        <ng-container matColumnDef="priority">
          <th mat-header-cell *matHeaderCellDef>Priority</th>
          <td mat-cell *matCellDef="let ticket">
            <span class="priority-badge" 
                  [style.background-color]="getPriorityColor(ticket.priority!)"
                  [style.color]="ticket.priority === 'critical' || ticket.priority === 'high' ? '#fff' : '#000'">
              {{ getPriorityText(ticket.priority!) }}
            </span>
          </td>
        </ng-container>

        <!-- Assigned To Column -->
        <ng-container matColumnDef="assigned_to">
          <th mat-header-cell *matHeaderCellDef>Assigned To</th>
          <td mat-cell *matCellDef="let ticket">
            <div class="assigned-info">
              <span class="assigned-name">{{ ticket.assigned_user_name || 'Unassigned' }}</span>
              <span class="technician" *ngIf="ticket.technician">via {{ ticket.technician }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Created Date Column -->
        <ng-container matColumnDef="created_at">
          <th mat-header-cell *matHeaderCellDef>Created</th>
          <td mat-cell *matCellDef="let ticket">
            <div class="date-info">
              <div class="date">{{ formatDate(ticket.created_at!) }}</div>
              <div class="created-by" *ngIf="ticket.created_by_name">
                by {{ ticket.created_by_name }}
              </div>
            </div>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let ticket">
            <div class="actions-container">
              <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="Actions">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="viewTicket(ticket)">
                  <mat-icon>visibility</mat-icon>
                  <span>View Details</span>
                </button>
                <button mat-menu-item (click)="editTicket(ticket)">
                  <mat-icon>edit</mat-icon>
                  <span>Edit Ticket</span>
                </button>
                <button mat-menu-item (click)="deleteTicket(ticket)" class="delete-action">
                  <mat-icon>delete</mat-icon>
                  <span>Delete</span>
                </button>
              </mat-menu>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
            class="ticket-row"
            (click)="viewTicket(row)"></tr>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-section" *ngIf="!loading && tickets.length > 0">
      <mat-paginator 
        [length]="totalRecords"
        [pageSize]="pageSize"
        [pageSizeOptions]="pageSizeOptions"
        [pageIndex]="currentPage - 1"
        (page)="onPageSizeChange($event)"
        showFirstLastButtons>
      </mat-paginator>
    </div>
  </div>
</div>
